import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import moment from 'moment-timezone';
import {
  format,
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import { FormattedMessage } from '../../i18n/FormattedMessage';
import AriesTable from '../../components_2/AriesTable';
import AriesDateNavigator from '../../components_2/AriesDateNavigator';
import { ItemsNeededIcon } from '../../components_2/AriesIcons';
import TaskListHeaderAries from '../TaskListLayout/TaskListHeaderAries';
import MyJobsRowAries from './MyJobsRowAries';

const HEADER_FIELDS = [
  { title: 'Job Id', isSortable: false, width: '10%' },
  { title: 'Site Name', isSortable: true, width: '15%' },
  { title: 'Job Type', isSortable: false, width: '13%' },
  { title: 'Job Kind', isSortable: false, width: '12%' },
  { title: 'Assigned To', isSortable: false, width: '15%' },
  { title: 'Due Date', isSortable: true, width: '12%' },
  { title: 'Status', isSortable: false, width: '12%' },
  { title: ' ', isSortable: false, width: '11%' },
];

const isMobileWidth = () =>
  globalThis.window !== undefined && globalThis.window.innerWidth <= 767;

const MyJobsLayout = ({
  setSearchText,
  setFilters,
  isCrossVisible,
  handleSearch,
  reportType,
  setDatePeriod,
  setReportType,
  sortData,
  jobList,
  isfetching,
  endMessage,
  handleStartJob,
  reportCategories,
  loadMore,
  hasMoreItems,
  showTaskModdal,
  runCoachMarks,
  isReportNeeded,
  toggleCoachmarksVisibility,
  currentCoachmarkTarget,
  printPage,
  searchText,
  auth,
  viewMode,
  setViewMode,
  intl,
}) => {
  const { timeZone } = auth.login.user;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isMobile, setIsMobile] = useState(isMobileWidth());

  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileWidth());
    globalThis.window.addEventListener('resize', handleResize);
    return () => globalThis.window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (viewMode) {
      setCurrentDate(new Date());
    }
  }, [viewMode]);

  const getFormattedLabel = () => {
    if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      return `${format(weekStart, 'MMM d')} - ${format(
        weekEnd,
        'MMM d, yyyy'
      )}`;
    }
    if (viewMode === 'month') {
      return format(currentDate, 'MMMM yyyy');
    }
    if (isMobile) {
      return `${format(currentDate, 'EEE')}. ${format(currentDate, 'PP')}`;
    }
    return format(currentDate, 'EEEE, PP');
  };

  const navigate = (direction) => {
    setCurrentDate((prevDate) => {
      let newDate;
      if (viewMode === 'week') {
        newDate =
          direction === 'prev' ? subWeeks(prevDate, 1) : addWeeks(prevDate, 1);
      } else if (viewMode === 'month') {
        newDate =
          direction === 'prev'
            ? subMonths(prevDate, 1)
            : addMonths(prevDate, 1);
      } else {
        newDate =
          direction === 'prev' ? subDays(prevDate, 1) : addDays(prevDate, 1);
      }

      const date = moment.tz(newDate, timeZone);
      let start;
      let end;

      if (viewMode === 'week') {
        start = date.clone().startOf('week');
        end = date.clone().endOf('week');
      } else if (viewMode === 'month') {
        start = date.clone().startOf('month');
        end = date.clone().endOf('month');
      } else {
        start = date.clone().startOf('day');
        end = date.clone().endOf('day');
      }

      setDatePeriod(start, end, true);
      return newDate;
    });
  };

  return (
    <div className="aries-my-jobs">
      <TaskListHeaderAries
        searchText={searchText}
        isCrossVisible={isCrossVisible}
        setSearchText={setSearchText}
        handleSearch={handleSearch}
        reportType={reportType}
        setReportType={setReportType}
        setFilters={setFilters}
        setDatePeriod={setDatePeriod}
        printPage={printPage}
        reportCategories={reportCategories}
        minDate={moment(new Date())}
        viewMode={viewMode}
        setViewMode={setViewMode}
        hideTypeFilter
        dateNavigator={
          <AriesDateNavigator
            label={getFormattedLabel()}
            onPrev={() => navigate('prev')}
            onNext={() => navigate('next')}
          />
        }
        extraActions={
          <span className="aries-my-jobs-items-needed">
            <ItemsNeededIcon size={18} className="aries-my-jobs-items-icon" />
            <span className="aries-my-jobs-items-text">
              <FormattedMessage
                id="Items Needed"
                defaultMessage="Items Needed"
              />
              :{' '}
            </span>
            <span
              className="aries-my-jobs-items-count aries-sample-badge text-xs font-medium rounded px-1.5 py-0.5"
              title={intl.formatMessage({
                id: 'Sample data',
                defaultMessage: 'Sample data',
              })}
            >
              5
            </span>
          </span>
        }
      />
      <div className="aries-cards">
        <AriesTable
          headerData={HEADER_FIELDS}
          bodyData={jobList}
          RowToRender={MyJobsRowAries}
          RowToRenderProps={{
            handleStartJob,
            showTaskModdal,
            runCoachMarks,
            currentCoachmarkTarget,
            toggleCoachmarksVisibility,
            isReportNeeded,
          }}
          sortData={sortData}
          loadMore={loadMore}
          hasMoreItems={hasMoreItems}
          isLoaderVisible={isfetching}
          isEndMessageVisible={jobList.length === 0}
          endMessage={endMessage}
          height={isMobile ? null : 'fill'}
        />
      </div>
    </div>
  );
};

MyJobsLayout.propTypes = {
  setSearchText: PropTypes.func,
  setFilters: PropTypes.func,
  handleSearch: PropTypes.func,
  reportType: PropTypes.string,
  setDatePeriod: PropTypes.func,
  setReportType: PropTypes.func,
  sortData: PropTypes.func,
  jobList: PropTypes.array,
  loadMore: PropTypes.func,
  isfetching: PropTypes.bool,
  hasMoreItems: PropTypes.bool,
  endMessage: PropTypes.string,
  isCrossVisible: PropTypes.bool,
  reportCategories: PropTypes.object,
  showTaskModdal: PropTypes.func,
  handleStartJob: PropTypes.object,
  runCoachMarks: PropTypes.func,
  isReportNeeded: PropTypes.any,
  toggleCoachmarksVisibility: PropTypes.func,
  currentCoachmarkTarget: PropTypes.string,
  searchText: PropTypes.string,
  auth: PropTypes.object,
  viewMode: PropTypes.string,
  setViewMode: PropTypes.func,
  printPage: PropTypes.func,
  intl: PropTypes.object,
};
export default injectIntl(MyJobsLayout);
